'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Alert } from '@/components/common/Alert';
import { FormInput } from '@/components/forms/FormInput';
import { ImageUpload } from '@/components/common/ImageUpload';
import { BottomSheet } from '@/components/common/BottomSheet';
import { handleError } from '@/lib/utils/error';
import type { Player, Tournament } from '@/lib/types/auction';

const editProfileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  profileImage: z.string().optional(),
});

type EditProfileInput = z.infer<typeof editProfileSchema>;

interface EditProfileModalProps {
  player: Player;
  tournament: Tournament;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    firstName: string;
    lastName: string;
    profilePicture?: string;
    tournamentId: string;
  }) => Promise<void>;
}

export function EditProfileModal({
  player,
  tournament,
  isOpen,
  onClose,
  onSave,
}: EditProfileModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | undefined>(
    player.user?.profilePicture
  );
  const [isUploading, setIsUploading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditProfileInput>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      name: player.user?.name || '',
    },
  });

  const onSubmit = async (data: EditProfileInput) => {
    try {
      setError(null);
      await onSave({
        firstName: data.name?.split(' ')[0],
        lastName: data.name?.split(' ').slice(1).join(' '),
        profilePicture: uploadedImageUrl,
        tournamentId: tournament.id,
      });
      onClose();
    } catch (err) {
      setError(handleError(err, 'Profile Update'));
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Player Profile"
      className="sm:max-w-md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="relative">
          <ImageUpload
            initialImage={player.user?.profilePicture}
            tag="player"
            secure={false}
            onChange={setUploadedImageUrl}
            onError={setError}
            shape="circle"
            fallbackText={player.user?.name || ''}
          />
        </div>

        <FormInput
          label="Full Name"
          name="name"
          register={register}
          error={errors.name?.message}
          leftIcon={<User className="h-4 w-4 text-gray-500" />}
          placeholder="Player Name"
        />

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-3">
          <Button variant="light" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || isUploading}>
            Save Changes
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}
